import { Box, Stack, Typography, useTheme } from "@mui/material";
import { TextV2Icon } from "components/icons";
import { Editor } from "@tiptap/core";

export const HighlightPopover = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose?: () => void;
}) => {
  const theme = useTheme();
  const colorList = [
    theme.palette.lightBg.high,
    theme.palette.lightBg.low,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette["warning-minor"].main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.primitive.system.magenta[800],
    theme.palette.primitive.system.violet[800],
    theme.palette.primitive.system.blue[800],
  ];
  const backgroundColorList = [
    "#FFFFFF",
    theme.palette.neutral["005"],
    theme.palette.secondary["purple-tint-3"],
    theme.palette.success.tint,
    theme.palette["warning-minor"].tint,
    theme.palette.warning.tint,
    theme.palette.error.tint,
    theme.palette.primitive.system.magenta[50],
    theme.palette.primitive.brand.purple[100],
    theme.palette.primitive.system.blue[100],
  ];
  return (
    <Stack
      sx={{
        width: "210px",
        height: "240px",
        border: `1px solid ${theme.palette.neutral["020"]}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#FFF",
        boxShadow: "-4px 9px 11px 0px #00000014",
      }}
    >
      <Box sx={{ p: "8px 16px" }}>
        <Typography variant="body-small" color={theme.palette.lightBg.low}>
          Text Color
        </Typography>
      </Box>
      <Stack direction={"row"} flexWrap={"wrap"} gap="8px" p="8px">
        {colorList.map((color) => (
          <TextColorItem
            key={color}
            color={color || ""}
            onClick={(color) => {
              editor.chain().focus().setColor(color).run();
              onClose?.();
            }}
          />
        ))}
      </Stack>

      <Box sx={{ p: "8px 16px" }}>
        <Typography variant="body-small" color={theme.palette.lightBg.low}>
          Background Color
        </Typography>
      </Box>
      <Stack direction={"row"} flexWrap={"wrap"} gap="8px" p="8px">
        {backgroundColorList.map((color) => (
          <BackgroundColorItem
            key={color}
            color={color || ""}
            onClick={(color) => {
              editor.chain().focus().setHighlight({ color }).run();
              onClose?.();
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const TextColorItem = ({
  color,
  onClick,
}: {
  color: string;
  onClick: (color: string) => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        cursor: "pointer",
        borderRadius: "6px",
        padding: "4px",
        outline: "none",
        height: "32px",
        width: "32px",
        backgroundColor: "#FFF",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        ":hover": {
          border: `1px solid ${theme.palette.lightBg.high}`,
        },
      }}
      onClick={(e) => {
        e.preventDefault();
        onClick(color);
        e.stopPropagation();
      }}
      component="button"
    >
      <TextV2Icon fontSize="small" htmlColor={color} />
    </Stack>
  );
};

const BackgroundColorItem = ({
  color,
  onClick,
}: {
  color: string;
  onClick: (color: string) => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        cursor: "pointer",
        borderRadius: "6px",
        padding: "4px",
        outline: "none",
        height: "32px",
        width: "32px",
        backgroundColor: color,
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        ":hover": {
          border: `1px solid ${theme.palette.lightBg.high}`,
        },
      }}
      onClick={(e) => {
        e.preventDefault();
        onClick(color);
        e.stopPropagation();
      }}
      component="button"
    ></Stack>
  );
};
